import { useMemo } from 'react';

import { placeHolder } from '@/services/config';
import { useWelfareController_allImages } from '@/services/hooks';
import type { FullSizeImage } from '@/services/types';
import { getImagesByIndicatorKey } from '@/utils/mapImagesByIndicatorKey';

import { type ImagesFishGroupScope, isScopeReady } from './fishGroupScope';
import { previousImagesOfFishGroup, welfareImagesParams } from './useGetWelfareImages';

/**
 * The daypart Health detections are drawn from. The images are requested for it, and the
 * `showImageViewer` flag that labels them must come from the same daypart's estimate.
 */
export const HEALTH_IMAGES_DAYPART_TAG = 'day';

export interface HealthImages {
    /**
     * API rows grouped by the indicator they carry a score for (`maturity_score`, ...). Opaque on
     * purpose: they go straight into `useImageState` with a mapper from this folder, which is
     * what gives them a shape.
     */
    imagesByIndicator: Record<string, unknown[]>;
    isLoading: boolean;
    isFetching: boolean;
}

export interface HealthImagesParams {
    /**
     * The fish group the page is about (`usePenCohortFishGroup()`). The request waits until it
     * is known; a failed lookup leaves the viewer empty rather than showing the whole cage.
     */
    fishGroup?: ImagesFishGroupScope;
}

/**
 * Day-part color welfare images of a cage, grouped by Laksvel indicator, for the Health
 * detections viewer. Same request the Health pages used to make through `LaksvelProvider`.
 */
export function useGetHealthImages(cageId: number, params: HealthImagesParams = {}): HealthImages {
    const { fishGroup } = params;
    const scopeReady = isScopeReady(fishGroup);
    const {
        data,
        isPending: isLoading,
        isFetching,
    } = useWelfareController_allImages(
        cageId,
        welfareImagesParams({
            type: 'color-welfare-image',
            tags: [HEALTH_IMAGES_DAYPART_TAG],
            fishGroupId: fishGroup?.fishGroupId,
        }),
        {
            // The previous pen's images while the next pen's load, never another fish group's;
            // an empty list on the first load.
            placeholderData: previousImagesOfFishGroup(
                fishGroup?.fishGroupId,
                placeHolder<FullSizeImage[]>([])
            ),
            enabled: cageId > 0 && scopeReady,
        }
    );

    // Until the fish group is known the key is the cage-wide one, whose cache holds the other
    // groups' images too: show none of it, as the empty list a first load starts from.
    const rows = scopeReady ? data?.data : undefined;
    const imagesByIndicator = useMemo(
        () => (rows ? getImagesByIndicatorKey(rows, cageId) : {}),
        [rows, cageId]
    );

    return {
        imagesByIndicator,
        isLoading: scopeReady && isLoading,
        isFetching: scopeReady && isFetching,
    };
}
